import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Sidenav, Nav } from 'rsuite';
import { useAuth } from '../context/AuthContext';
import {
    LayoutDashboard,
    FileText,
    FileStack,
    Users,
    Settings,
    Bell,
    LogOut,
    User,
    Star
} from 'lucide-react';
import defaultLogo from '../assets/almaquinta.svg';

const Sidebar = ({ configuracion, expanded, onExpandedChange }) => {
    const { user, logout, tienePermiso } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const empresaNombre = configuracion?.empresa_nombre || 'Alma Industria Creativa';

    const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 1024);

    const setExpanded = (val) => {
        const newVal = typeof val === 'function' ? val(expanded) : val;
        onExpandedChange(newVal);
    };

    useEffect(() => {
        const handleResize = () => {
            const small = window.innerWidth < 1024;
            setIsSmallScreen(small);
            onExpandedChange(!small);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [onExpandedChange]);

    // Logo URL - usa el logo de configuracion si existe, sino el default
    const getLogoUrl = () => {
        const logo = configuracion?.empresa_logo;
        if (!logo) return defaultLogo;
        if (logo.startsWith('data:') || logo.startsWith('http')) return logo;
        const baseUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';
        return `${baseUrl}${logo}`;
    };

    const isAdmin = user?.rol === 'admin' || user?.rol === 'superadmin';

    // Active key basado en la ruta actual
    const getActiveKey = () => {
        const path = location.pathname;
        if (path === '/dashboard') return 'dashboard';
        if (path === '/cotizaciones/nueva') return 'cot-nueva';
        if (path === '/cotizaciones/mis-cotizaciones') return 'cot-mis';
        if (path === '/cotizaciones/clientes') return 'cot-clientes';
        if (path.startsWith('/cotizaciones/cliente/')) return 'cot-clientes';
        if (path === '/cotizaciones') return 'cot-listado';
        if (path.startsWith('/cotizaciones/')) return 'cot-listado';
        if (path.startsWith('/plantillas')) return 'plantillas';
        if (path === '/clientes') return 'clientes';
        if (path === '/alertas') return 'alertas';
        if (path === '/perfil') return 'perfil';
        if (path === '/servicios') return 'servicios';
        if (path === '/personal') return 'personal';
        if (path === '/costos-fijos') return 'costos-fijos';
        if (path === '/configuracion') return 'configuracion';
        if (path === '/usuarios') return 'usuarios';
        if (path === '/roles') return 'roles';
        return 'dashboard';
    };

    // Rutas de administración
    const adminPaths = ['/servicios', '/personal', '/costos-fijos', '/configuracion', '/usuarios', '/alertas', '/roles'];
    const isAdminPath = adminPaths.some(p => location.pathname === p);

    // Submenu abierto
    const getInitialOpenKeys = () => {
        const keys = [];
        if (location.pathname.startsWith('/cotizaciones')) keys.push('cotizaciones');
        if (isAdminPath) keys.push('admin');
        return keys;
    };

    const [openKeys, setOpenKeys] = useState(getInitialOpenKeys);

    useEffect(() => {
        if (location.pathname.startsWith('/cotizaciones')) {
            setOpenKeys(prev => prev.includes('cotizaciones') ? prev : [...prev, 'cotizaciones']);
        }
        if (adminPaths.some(p => location.pathname === p)) {
            setOpenKeys(prev => prev.includes('admin') ? prev : [...prev, 'admin']);
        }
    }, [location.pathname]);

    // Navegación
    const routeMap = {
        'dashboard': '/dashboard',
        'cot-nueva': '/cotizaciones/nueva',
        'cot-mis': '/cotizaciones/mis-cotizaciones',
        'cot-listado': '/cotizaciones',
        'cot-clientes': '/cotizaciones/clientes',
        'plantillas': '/plantillas',
        'clientes': '/clientes',
        'alertas': '/alertas',
        'perfil': '/perfil',
        'servicios': '/servicios',
        'personal': '/personal',
        'costos-fijos': '/costos-fijos',
        'configuracion': '/configuracion',
        'usuarios': '/usuarios',
        'alertas-admin': '/alertas',
        'roles': '/roles',
    };

    const handleSelect = (eventKey) => {
        if (routeMap[eventKey]) {
            navigate(routeMap[eventKey]);
            if (isSmallScreen) {
                setExpanded(false);
            }
        }
    };

    return (
        <>
            {/* Overlay cuando sidebar expandido en mobile */}
            {isSmallScreen && expanded && (
                <div className="sidebar-overlay" onClick={() => setExpanded(false)} />
            )}

            <aside className={`app-sidebar ${expanded ? '' : 'app-sidebar--collapsed'}`}>
                {/* Logo */}
                <div className="sidebar-logo-section">
                    <img className="sidebar-logo" src={getLogoUrl()} alt="Logo" />
                    {expanded && (
                        <div>
                            <h1 className="sidebar-title">Don Presupuesto</h1>
                            <p className="sidebar-subtitle">{empresaNombre}</p>
                        </div>
                    )}
                </div>

                {/* Navegación + Toggle */}
                <Sidenav
                    expanded={expanded}
                    appearance="subtle"
                    openKeys={openKeys}
                    onOpenChange={setOpenKeys}
                    className="sidebar-sidenav"
                >
                    <Sidenav.Body>
                        <Nav activeKey={getActiveKey()} onSelect={handleSelect}>
                            <Nav.Item eventKey="dashboard" icon={<LayoutDashboard size={18} />}>
                                Inicio
                            </Nav.Item>
                            <Nav.Menu
                                eventKey="cotizaciones"
                                title="Cotizaciones"
                                icon={<FileText size={18} />}
                            >
                                {tienePermiso('gestion_cotizaciones') && <Nav.Item eventKey="cot-nueva">Nueva Cotización</Nav.Item>}
                                <Nav.Item eventKey="cot-mis" style={{ color: '#eab308' }}>
                                    <Star size={12} style={{ marginRight: 6, verticalAlign: 'middle', fill: '#eab308', stroke: '#eab308' }} />
                                    Mis Cotizaciones
                                </Nav.Item>
                                {tienePermiso('gestion_cotizaciones') && <Nav.Item eventKey="cot-listado">Listado</Nav.Item>}
                                {tienePermiso('gestion_cotizaciones') && <Nav.Item eventKey="cot-clientes">Por Cliente</Nav.Item>}
                            </Nav.Menu>
                            {tienePermiso('gestion_plantillas') && (
                                <Nav.Item eventKey="plantillas" icon={<FileStack size={18} />}>
                                    Plantillas
                                </Nav.Item>
                            )}
                            {tienePermiso('gestion_clientes') && (
                                <Nav.Item eventKey="clientes" icon={<Users size={18} />}>
                                    Clientes
                                </Nav.Item>
                            )}
                            {tienePermiso('seguimientos') && (
                                <Nav.Item eventKey="alertas" icon={<Bell size={18} />}>
                                    Alertas
                                </Nav.Item>
                            )}
                            {(tienePermiso('gestion_servicios') || tienePermiso('gestion_personal') || tienePermiso('gestion_costos_fijos') || tienePermiso('configuracion') || tienePermiso('configuracion_alertas') || tienePermiso('gestion_usuarios') || tienePermiso('gestion_roles')) && (
                            <Nav.Menu
                                eventKey="admin"
                                title="Administración"
                                icon={<Settings size={18} />}
                            >
                                {tienePermiso('gestion_servicios') && <Nav.Item eventKey="servicios">Servicios</Nav.Item>}
                                {tienePermiso('gestion_personal') && <Nav.Item eventKey="personal">Personal</Nav.Item>}
                                {tienePermiso('gestion_costos_fijos') && <Nav.Item eventKey="costos-fijos">Costos Fijos</Nav.Item>}
                                {tienePermiso('configuracion') && <Nav.Item eventKey="configuracion">Configuración</Nav.Item>}
                                {tienePermiso('configuracion_alertas') && <Nav.Item eventKey="alertas-admin">Alertas</Nav.Item>}
                                {tienePermiso('gestion_usuarios') && <Nav.Item eventKey="usuarios">Usuarios</Nav.Item>}
                                {tienePermiso('gestion_roles') && <Nav.Item eventKey="roles">Roles y Permisos</Nav.Item>}
                            </Nav.Menu>
                            )}
                        </Nav>
                    </Sidenav.Body>
                    <Sidenav.Footer>
                        <Sidenav.Toggle onToggle={setExpanded} />
                    </Sidenav.Footer>
                </Sidenav>

                {/* Usuario */}
                <div className="sidebar-user-section">
                    <div
                        className="sidebar-user-info sidebar-user-link"
                        onClick={() => { navigate('/perfil'); if (isSmallScreen) setExpanded(false); }}
                        title="Mi Perfil"
                    >
                        <User size={16} />
                        {expanded && <span className="sidebar-user-name">{user?.nombre}</span>}
                    </div>
                    <button className="sidebar-logout-btn" onClick={logout} title="Salir">
                        <LogOut size={16} />
                        {expanded && <span>Salir</span>}
                    </button>
                </div>
            </aside>
        </>
    );
};

export default Sidebar;
